import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useInstructorMode } from '../hooks/useInstructorMode';
import { formatDate } from '../utils/dateUtils';
import './SongView.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function SongView() {
  const { id } = useParams();
  const { buildUrl, isInstructorMode } = useInstructorMode();
  
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSong = async () => {
      try {
        setLoading(true);
        
        // Use backend param (?show=all) when in instructor mode
        let url = `${API_BASE}/songs/${id}`;
        if (isInstructorMode) {
          url += '?show=all';
        }
        
        const response = await axios.get(url);
        setSong(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load song');
        console.error('Error fetching song:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSong();
  }, [id, isInstructorMode]);

  if (loading) {
    return (
      <div className="song-view loading">
        <div className="spinner"></div>
        <p>Loading song...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="song-view error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="song-view error">
        <h2>Song not found</h2>
      </div>
    );
  }

  return (
    <div className="song-view">
      <div className="song-header">
        <h1>{song.name}</h1>
        <div className="song-artists">
          by {song.artists.map((artist, index) => (
            <span key={artist.id}>
              <a href={buildUrl(`/artist/${artist.id}`)} className="artist-link">
                {artist.name}
              </a>
              {index < song.artists.length - 1 && ', '}
            </span>
          ))}
        </div>
        <div className="song-stats">
          {song.bpm && isInstructorMode && (
            <span className="stat-badge instructor-bpm">🎵 {song.bpm} BPM</span>
          )}
          <span className="stat-badge">▶️ {song.total_plays || 1} plays</span>
          {song.is_local && <span className="stat-badge local">📁 Local File</span>}
        </div>
        {isInstructorMode && (song.opener_count > 0 || song.slow_song_count > 0 || song.sprint_count > 0 || song.closer_count > 0) && (
          <div className="special-roles-stats">
            <h3>Special Role Usage</h3>
            <div className="role-badges">
              {song.opener_count > 0 && (
                <span className="role-badge opener">
                  Opener: {song.opener_count}x
                </span>
              )}
              {song.slow_song_count > 0 && (
                <span className="role-badge slow">
                  Slow Song: {song.slow_song_count}x
                </span>
              )}
              {song.sprint_count > 0 && (
                <span className="role-badge sprint">
                  Sprint: {song.sprint_count}x
                </span>
              )}
              {song.closer_count > 0 && (
                <span className="role-badge closer">
                  Closer: {song.closer_count}x
                </span>
              )}
            </div>
          </div>
        )}
        {song.spotify_url && (
          <a 
            href={song.spotify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="spotify-button"
          >
            Open in Spotify
          </a>
        )}
      </div>

      <div className="play-history">
        <h2>Play History</h2>
        <p className="history-subtitle">
          This song has been played {song.playlists?.length || 0} time{song.playlists?.length !== 1 ? 's' : ''}
        </p>
        <div className="history-list">
          {song.playlists && song.playlists.length > 0 ? (
            song.playlists.map((play) => (
              <a 
                key={play.id}
                href={buildUrl(`/playlist/${play.id}`)}
                className="history-card"
              >
                <div className="history-date">
                  {formatDate(play.playlist_date)}
                </div>
                <div className="history-position">
                  Position #{play.position}
                </div>
              </a>
            ))
          ) : (
            <p>No plays recorded yet.</p>
          )}
        </div>
      </div>

      <div className="song-actions">
        <a href={buildUrl('/')} className="btn-back">← Back to All Playlists</a>
      </div>
    </div>
  );
}

export default SongView;